import { z } from "zod";

// CAR-63 — central registry of API error codes + the canonical error
// response body shape. Both apps (mobile, admin) and the API import from
// here so a rename to one code surfaces as a TypeScript error everywhere
// it's pattern-matched, instead of the pre-fix string-includes pattern
// that silently broke when the API renamed `too_early`, etc.
//
// Codes are grouped by domain. The flat `ERROR_CODES` const carries the
// machine-readable identifier; the `messageKey` is an i18n lookup the
// client renders (mobile: `apps/mobile/src/i18n/locales/{sv,en}.json`,
// admin: hardcoded Swedish copy maps in routes). The pair is the contract.

export const ERROR_CODES = {
  // Auth
  MISSING_TOKEN: "missing_token",
  INVALID_TOKEN: "invalid_token",
  NOT_A_CLINICIAN: "not_a_clinician",
  // TES-281: RolesGuard 403 — authenticated staff whose role isn't in the
  // endpoint's @Roles() set.
  NOT_AUTHORIZED: "not_authorized",
  // TES-281: an Employee row whose role column is outside EMPLOYEE_ROLES —
  // sign-in refuses to mint a token for an unrecognizable role.
  ROLE_NOT_RECOGNIZED: "role_not_recognized",

  // TES-288 — manual flow governance
  // A manual (re-)assign or chat-draft approval hit a PAUSED assignment; the
  // admin should offer "resume" instead of treating the no-op as success.
  FLOW_ASSIGNMENT_PAUSED: "flow_assignment_paused",
  // The published version the clinician previewed was superseded between
  // preview and confirm (TOCTOU) — re-open the preview.
  STALE_FLOW_PREVIEW: "stale_flow_preview",
  TEST_ONLY_FLOW: "test_only_flow",
  // TES-592 — CP-24 is feature-flagged off (CP24_SAFETY_ROUTE_ENABLED).
  CP24_DISABLED: "cp24_disabled",
  // TES-591 — member content / tools without a verified subscription.
  SUBSCRIPTION_REQUIRED: "subscription_required",
  CSRF_ORIGIN_MISMATCH: "csrf_origin_mismatch",
  INVALID_DOCTOR_KEY: "invalid_doctor_key",
  DOCTOR_KEY_NOT_CONFIGURED: "doctor_key_not_configured",
  NO_DEV_DOCTOR_PROVISIONED: "no_dev_doctor_provisioned",
  INVALID_INVITE_CODE: "invalid_invite_code",
  BANKID_REAUTH_REQUIRED: "bankid_reauth_required",
  BANKID_SESSION_NOT_FOUND: "bankid_session_not_found",

  // Tenant
  TENANT_UNRESOLVED: "tenant_unresolved",

  // Booking
  SLOT_TAKEN: "slot_taken",
  TOO_EARLY: "too_early",
  // TES-521 — the meeting has ended (or was cancelled / marked no-show).
  MEETING_OVER: "meeting_over",
  // TES-1383 — a join refused because the meeting was cancelled. The
  // rescheduled variant also carries `newMeetingId` and `newScheduledAt`.
  MEETING_CANCELLED: "meeting_cancelled",
  MEETING_RESCHEDULED: "meeting_rescheduled",
  NOT_YOUR_MEETING: "not_your_meeting",
  MEETING_NOT_FOUND: "meeting_not_found",

  // Cycle
  CYCLE_NOT_FOUND: "cycle_not_found",
  CONCURRENT_TRANSITION: "concurrent_transition",

  // Patient / Doctor
  PATIENT_NOT_FOUND: "patient_not_found",
  // TES-534 — the patient erased their account (Art. 17); their meetings are
  // over and cannot be documented.
  PATIENT_ERASED: "patient_erased",
  // TES-522 — F-01 result decisions.
  F01_NOT_DONE: "f01_not_done",
  F01_NO_SAVED_CARD: "f01_no_saved_card",
  F01_INVALID_CHOICE: "invalid_choice",
  // Ö-64 — an e-mail address belongs to one patient.
  EMAIL_TAKEN: "email_taken",
  // TES-504 — "send the link again" needs an address to send it to, and
  // an already-verified address has nothing to confirm.
  EMAIL_MISSING: "email_missing",
  EMAIL_ALREADY_VERIFIED: "email_already_verified",
  DOCTOR_NOT_FOUND: "doctor_not_found",

  // Forms / intake template
  FORM_TEMPLATE_NOT_FOUND: "form_template_not_found",
  INTAKE_TEMPLATE_NOT_FOUND: "intake_template_not_found",
  INTAKE_DRAFT_NOT_FOUND: "intake_draft_not_found",
  INTAKE_TEMPLATE_CORRUPT: "intake_template_corrupt",
  INTAKE_DRAFT_GUARDRAIL_FAILED: "intake_draft_guardrail_failed",
  QUESTION_REGISTER_MISMATCH: "question_register_mismatch",
  VALIDATION_FAILED: "validation_failed",

  // Webhooks
  INVALID_WEBHOOK: "invalid_webhook",
  INVALID_WEBHOOK_BODY: "invalid_webhook_body",
  INVALID_WEBHOOK_SECRET: "invalid_webhook_secret",
  MISSING_WEBHOOK_SECRET: "missing_webhook_secret",
  MISSING_SIGNATURE_HEADERS: "missing_signature_headers",
  MISSING_STRIPE_SIGNATURE: "missing_stripe_signature",
  INVALID_LAB_RESULT_PAYLOAD: "invalid_lab_result_payload",
  // TES-283 — lab feedback layer guards
  // Send refused: the clinician layer is empty (the AI draft no longer
  // auto-fills, so an untouched panel must not reach the patient).
  LAB_FEEDBACK_EMPTY: "lab_feedback_empty",
  // Draft-save refused on an already-sent order — the patient reads these
  // columns live; updates must go through send (re-send), which audits.
  LAB_FEEDBACK_ALREADY_SENT: "lab_feedback_already_sent",
  // TES-1370 (Ö-582) — publish refused: the summary, or a non-empty
  // explanation of a value, is not approved by the doctor as it stands.
  LAB_FEEDBACK_NOT_APPROVED: "lab_feedback_not_approved",
  // TES-1370 — approval named a value that is not in the order.
  LAB_FEEDBACK_UNKNOWN_ANALYTE: "lab_feedback_unknown_analyte",

  // Billing
  BILLING_NOT_CONFIGURED: "billing_not_configured",

  // Health / measurements
  INVALID_KG: "invalid_kg",
  MISSING_KG: "missing_kg",
  INVALID_CM: "invalid_cm",
  INVALID_WEIGHT: "invalid_weight",
  MISSING_MEASUREMENT: "missing_measurement",
  INVALID_LOGGED_FOR: "invalid_logged_for",

  // Chat
  EMPTY_MESSAGE: "empty_message",
  INVALID_KIND: "invalid_kind",
  INVALID_ACTION: "invalid_action",
  INVALID_SINCE: "invalid_since",

  // Misc
  MISSING_FIELDS: "missing_fields",
  INVALID_JSON: "invalid_json",
  INVALID_FCM_TOKEN: "invalid_fcm_token",
  INVALID_DOING_WELL: "invalid_doing_well",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

// The canonical API error body. Routes throw HttpException variants whose
// body matches this shape; clients parse + branch on `code` (which they
// import from this file as a constant, not a string literal).
export const apiErrorBodySchema = z.object({
  error: z.string(),
  messageKey: z.string().optional(),
});

export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>;

// Convenience helper for clients: narrow an unknown thrown value to the
// API's structured error body when possible, else return null. The mobile
// + admin fetch wrappers already turn the body's `error` into a thrown
// Error message; this helper recovers it.
export function tryParseApiError(value: unknown): ApiErrorBody | null {
  const parsed = apiErrorBodySchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
